import {
  TOOLBOX_LATEST_PATH,
  toolboxLatestReleaseSchema,
  type ToolboxLatestRelease,
  type ToolboxReleaseFile,
} from "@ttc-spectree/toolbox-contract/contracts";
import { api } from "./client";

/**
 * Client for the SpecTree Toolbox download (epic PI-SPEC-SW-E288, feature
 * PI-SPEC-SW-1468, UX-PM-41):
 * - `GET /toolbox/latest`         the newest installer per platform and the release note
 * - `GET /toolbox/updates/:file`  one installer from the update feed
 *
 * Both need the person's SpecTree token, so a plain `<a href>` cannot
 * download an installer: it is fetched through the API client as a Blob and
 * saved from an object URL.
 */

interface DataResponse<T> {
  data: T;
}

/** The API path under `/api/v1` (the client adds the prefix). */
const LATEST_PATH = TOOLBOX_LATEST_PATH.replace(/^\/api\/v1/, "");

export type { ToolboxLatestRelease, ToolboxReleaseFile };

export const toolboxUpdatesApi = {
  /** The newest release; unknown keys are dropped by the lenient contract schema. */
  async latest(): Promise<ToolboxLatestRelease> {
    const response = await api.get<DataResponse<unknown>>(LATEST_PATH);
    return toolboxLatestReleaseSchema.parse(response.data);
  },

  /** The installer's bytes from the update feed. */
  installer(fileName: string): Promise<Blob> {
    return api.getBlob(`/toolbox/updates/${encodeURIComponent(fileName)}`);
  },
};

/** Fetch an installer with the person's token and save it under its own file name. */
export async function saveToolboxInstaller(fileName: string): Promise<void> {
  const blob = await toolboxUpdatesApi.installer(fileName);
  const blobUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = blobUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  } finally {
    // Revoke after the click has handed the URL to the download, not before.
    setTimeout(() => {
      URL.revokeObjectURL(blobUrl);
    }, 60_000);
  }
}

/** Whole megabytes, as the download page shows them ("98 MB"); at least 1 for a non-empty file. */
export function installerSizeMb(sizeBytes: number): number {
  if (sizeBytes <= 0) return 0;
  return Math.max(1, Math.round(sizeBytes / (1024 * 1024)));
}
